import React from "react";
import {Box, Dialog, IconButton, Stack, Typography} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import SearchIcon from "@mui/icons-material/Search";
import {useTranslation} from "react-i18next";
import {settingsSearchColors, SettingsSearchInput, SettingsSearchResults,} from "./SettingsSearchPrimitives";
import {useSettingsSearch} from "./useSettingsSearch";
import type {SettingsSearchProps, SettingsSearchSection} from "./settingsSearch.types";

type SettingsSearchDialogProps = SettingsSearchProps & {
    enableKeyboardShortcut?: boolean
    triggerVariant?: 'outlined' | 'text'
}

const keySx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 24,
    height: 24,
    px: 0.65,
    borderRadius: 1,
    bgcolor: settingsSearchColors.subtleSurface,
    border: `1px solid ${settingsSearchColors.border}`,
    boxShadow: '0 1px 0 rgba(31, 45, 65, 0.14)',
    color: settingsSearchColors.text,
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1,
    '& svg': {
        fontSize: 15,
    },
}

const FooterHint = ({children, label}: React.PropsWithChildren<{ label: string }>) => (
    <Stack direction="row" alignItems="center" gap={0.5}>
        {children}
        <Typography variant="body2" sx={{color: settingsSearchColors.muted}}>
            {label}
        </Typography>
    </Stack>
)

const SettingsSearchDialogContent = ({
                                         onClose,
                                         ...searchProps
                                     }: SettingsSearchProps & { onClose: () => void }) => {
    const {t} = useTranslation()
    const search = useSettingsSearch({...searchProps, autoFocus: true, onClose})
    const sections = React.useMemo<SettingsSearchSection[]>(() => [
        {title: 'Recent', results: search.displayedRecentResults},
        {title: 'Go to', results: search.goToResults},
        {title: 'Find', results: search.findResults},
        {title: 'Results', results: search.searchResults},
        {title: 'Create', results: search.createResults},
    ], [
        search.createResults,
        search.displayedRecentResults,
        search.findResults,
        search.goToResults,
        search.searchResults,
    ])

    return (
        <>
            <Box sx={{p: 1.25, pb: 1, flexShrink: 0}}>
                <SettingsSearchInput search={search} onClose={onClose}/>
            </Box>

            <Box sx={{display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden'}}>
                <SettingsSearchResults search={search} sections={sections}/>
            </Box>

            <Box
                component="footer"
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                    flexShrink: 0,
                    minHeight: 48,
                    px: 1.5,
                    display: {xs: 'none', md: 'flex'},
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    borderTop: '1px solid',
                    borderColor: settingsSearchColors.border,
                    bgcolor: settingsSearchColors.surface,
                }}
            >
                <Stack direction="row" alignItems="center" gap={2}>
                    <FooterHint label={t('Navigate')}>
                        <Stack direction="row" gap={0.5}>
                            <Box component="kbd" sx={keySx}>
                                <ArrowUpwardIcon/>
                            </Box>
                            <Box component="kbd" sx={keySx}>
                                <ArrowDownwardIcon/>
                            </Box>
                        </Stack>
                    </FooterHint>
                    <FooterHint label={t('Open')}>
                        <Box component="kbd" sx={keySx}>
                            <KeyboardReturnIcon/>
                        </Box>
                    </FooterHint>
                </Stack>

                <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="body2" sx={{color: settingsSearchColors.muted, mr: 0.25}}>
                        {t('Open search')}
                    </Typography>
                    <Box component="kbd" sx={keySx}>⌘</Box>
                    <Box component="kbd" sx={keySx}>K</Box>
                </Stack>
            </Box>
        </>
    )
}

const SettingsSearchDialog = ({
                                  enableKeyboardShortcut = true,
                                  triggerVariant = 'outlined',
                                  ...searchProps
                              }: SettingsSearchDialogProps) => {
    const {t} = useTranslation()
    const [open, setOpen] = React.useState(false)
    const close = React.useCallback(() => setOpen(false), [])

    React.useEffect(() => {
        if (!enableKeyboardShortcut) return

        const handleShortcut = (event: KeyboardEvent) => {
            if (event.key?.toLowerCase() !== 'k' || (!event.metaKey && !event.ctrlKey)) return
            event.preventDefault()
            setOpen(true)
        }

        window.addEventListener('keydown', handleShortcut)
        return () => window.removeEventListener('keydown', handleShortcut)
    }, [enableKeyboardShortcut])

    const handleDialogKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        close()
    }

    return (
        <>
            <IconButton
                aria-label={t('Search settings')}
                size="small"
                color="primary"
                data-dashboard-control=""
                disableRipple
                onClick={() => setOpen(true)}
                sx={[{
                    borderRadius: 2,
                    width: 36,
                    height: 36,
                }, triggerVariant === 'outlined' ? {
                    border: '1px solid',
                    borderColor: 'grey.200',
                    bgcolor: 'background.paper',
                    '&:hover': {
                        borderColor: 'grey.300',
                    },
                } : {
                    border: 0,
                    bgcolor: 'transparent',
                }]}
            >
                <SearchIcon fontSize="small"/>
            </IconButton>

            <Dialog
                open={open}
                onClose={close}
                onKeyDown={handleDialogKeyDown}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        display: 'flex',
                        flexDirection: 'column',
                        width: 680,
                        height: 'min(660px, calc(100vh - 96px))',
                        maxWidth: 'calc(100vw - 32px)',
                        overflow: 'hidden',
                        borderRadius: 2,
                        bgcolor: settingsSearchColors.surface,
                        border: '1px solid',
                        borderColor: settingsSearchColors.border,
                        boxShadow: '0 20px 70px rgba(31, 45, 65, 0.18)',
                    },
                }}
            >
                {open && <SettingsSearchDialogContent {...searchProps} onClose={close}/>}
            </Dialog>
        </>
    )
}

export default SettingsSearchDialog
